/**
 * Billing and subscription API functions.
 */

import axios from "axios";
import { backend_url } from "@/constants";
import { Endpoints } from "@/utils/api/endpoints";
import { AXIOS_CONFIG } from "@/utils/api";
import type {
  SubscriptionInfo,
  SubscriptionInfoResponse,
  BillingPortalInfo,
  BillingPortalResponse,
  FailedInvoiceInfo,
  FailedInvoiceResponse,
  CheckInvoicePaidInfo,
  CheckInvoicePaidResponse,
} from "./types";

/**
 * Get subscription information without creating billing portal sessions.
 *
 * This is a lightweight endpoint suitable for polling and frequent queries.
 * Returns current plan details, credit balance, and subscription status.
 *
 * USE THIS for:
 * - Polling for subscription updates
 * - Displaying current plan on dashboards
 * - Checking credit balance
 * - Frequent read-only queries
 *
 * @returns Promise resolving to subscription information
 * @throws Error if the request fails
 */
export const getSubscriptionInfo = async (): Promise<SubscriptionInfo> => {
  try {
    const response = await axios.get<SubscriptionInfoResponse>(
      `${backend_url}/${Endpoints.subscriptionInfo}`,
      {
        ...AXIOS_CONFIG,
      }
    );
    return response.data.content;
  } catch (error) {
    console.error("Error fetching subscription info:", error);
    throw error;
  }
};

/**
 * Get billing information; optionally create a Stripe billing portal or customer session.
 *
 * By default this may create a new Stripe billing portal session (or customer session when
 * there is no product). Pass ``skipPortal: true`` to only fetch ``current_plan`` and related
 * fields (e.g. ledger ``usage``) without creating any Stripe session.
 *
 * USE THIS for:
 * - User clicks "Manage Subscription" button (portal session)
 * - User wants to update payment method / cancel (portal with ``type``)
 * - Read-only flows that need billing ``current_plan`` shape (``skipPortal``)
 *
 * @param return_url - Optional URL to redirect to after portal session
 * @param type - Optional flow type: "subscription_cancel" or "subscription_update"
 * @param skipPortal - If true, sends ``skip_portal`` so the API returns data without a portal URL
 * @returns Promise resolving to billing portal information
 * @throws Error if the request fails
 */
export const getBillingInformation = async (
  return_url?: string,
  type?: string,
  skipPortal?: boolean
): Promise<BillingPortalInfo> => {
  try {
    const response = await axios.get<BillingPortalResponse>(
      `${backend_url}/${Endpoints.billing}`,
      {
        params: {
          return_url,
          type,
          ...(skipPortal ? { skip_portal: "true" } : {}),
        },
        ...AXIOS_CONFIG,
      }
    );
    return response.data.content;
  } catch (error) {
    console.error("Error fetching billing information:", error);
    throw error;
  }
};

/**
 * Get failed invoice information.
 *
 * This endpoint retrieves the details of the failed invoice that requires payment.
 * Returns invoice details including the hosted invoice URL where the user can pay.
 *
 * USE THIS when:
 * - User has requires_payment_update set to true
 * - Displaying the failed payment page
 * - Providing the pay now button link
 *
 * @returns Promise resolving to failed invoice information
 * @throws Error if the request fails or no failed invoice exists
 */
export const getFailedInvoice = async (): Promise<FailedInvoiceInfo> => {
  try {
    const response = await axios.get<FailedInvoiceResponse>(
      `${backend_url}/${Endpoints.failedInvoice}`,
      {
        ...AXIOS_CONFIG,
      }
    );
    return response.data.content;
  } catch (error) {
    console.error("Error fetching failed invoice:", error);
    throw error;
  }
};

/**
 * Check if the failed invoice has been paid.
 *
 * This endpoint verifies the invoice status directly with Stripe
 * and clears payment flags if the invoice is paid.
 *
 * USE THIS when:
 * - User clicks "I've already paid" button
 * - Verifying payment status before redirecting
 *
 * @returns Promise resolving to payment status
 * @throws Error if the request fails
 */
export const checkInvoicePaid = async (): Promise<CheckInvoicePaidInfo> => {
  try {
    const response = await axios.post<CheckInvoicePaidResponse>(
      `${backend_url}/${Endpoints.failedInvoice}`,
      {},
      {
        ...AXIOS_CONFIG,
      }
    );
    return response.data.content;
  } catch (error) {
    console.error("Error checking invoice status:", error);
    throw error;
  }
};

/**
 * Create a SetupIntent for the payment-update flow (Stripe Elements).
 * Returns clientSecret and publishableKey for mounting the Payment Element.
 *
 * @returns Promise resolving to { clientSecret, publishableKey }
 * @throws Error if the request fails or payment update not required
 */
export const createPaymentUpdateSetupIntent = async (): Promise<{
  clientSecret: string;
  publishableKey: string;
}> => {
  const response = await axios.post<{
    message: string;
    content: { clientSecret: string; publishableKey: string };
  }>(
    `${backend_url}/${Endpoints.paymentUpdateCreateSetupIntent}`,
    {},
    AXIOS_CONFIG
  );
  const content = response.data.content;
  return {
    clientSecret: content.clientSecret,
    publishableKey: content.publishableKey,
  };
};

/**
 * Create a SetupIntent for the contract-upgrade flow (collecting a new default card).
 * Returns clientSecret and publishableKey for mounting the Payment Element.
 *
 * @returns Promise resolving to { clientSecret, publishableKey }
 * @throws Error if the request fails
 */
export const createContractUpgradeSetupIntent = async (): Promise<{
  clientSecret: string;
  publishableKey: string;
}> => {
  const response = await axios.post<{
    message: string;
    content: { clientSecret: string; publishableKey: string };
  }>(
    `${backend_url}/${Endpoints.contractUpgradeCreateSetupIntent}`,
    {},
    AXIOS_CONFIG
  );
  const content = response.data.content;
  return {
    clientSecret: content.clientSecret,
    publishableKey: content.publishableKey,
  };
};

/**
 * Submit the new payment method and retry past_due invoices.
 * Sets customer invoice default, pays all past_due invoices, clears payment flags.
 *
 * @param paymentMethodId - Payment method ID from confirmed SetupIntent
 * @returns Promise resolving to { paymentSuccessful, paidCount, failedCount }
 */
export const submitPaymentMethodAndRetryInvoice = async (
  paymentMethodId: string
): Promise<{
  paymentSuccessful: boolean;
  paidCount?: number;
  failedCount?: number;
  message?: string;
}> => {
  const response = await axios.post<{
    message: string;
    content: {
      paymentSuccessful: boolean;
      paidCount?: number;
      failedCount?: number;
    };
  }>(
    `${backend_url}/${Endpoints.updatePayment}`,
    { payment_method_id: paymentMethodId },
    AXIOS_CONFIG
  );
  const content = response.data.content;
  return {
    paymentSuccessful: content.paymentSuccessful,
    paidCount: content.paidCount,
    failedCount: content.failedCount,
  };
};
